import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { Popper, TextField, Tooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { I18n } from 'aws-amplify';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Assignee.css';
import { createFilterOptions } from '@material-ui/lab';
import { PropTypes } from 'prop-types';
import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';

const filterSugguestionAssignOption = createFilterOptions({
    matchFrom: 'any',
    stringify: option => option.fullName
});

const handleKeyDown = (e, assigneeUserData) => {
    if (assigneeUserData && assigneeUserData.length > 0 && assigneeUserData[0]?.id !== Constant.OPTION_UNASSIGNEE.KEY) {
        e.preventDefault();
    }
};

const RenderOptionTag = ({ params, assigneeUserData }) => {
    let newParams = { ...params };

    return (
        <TextField
            {...newParams}
            className="form-control form-control--wrapSelect form-control--hg40"
            // placeholder={Utils.isEmptyArray(assigneeUserData) ? '検索キーワード' : ''}
            variant="outlined"
            onKeyDown={e => handleKeyDown(e, assigneeUserData)}
        />
    );
};

const getShortFullName = (fullName, maxLength) => {
    if (fullName && fullName.length > maxLength) {
        fullName = fullName.substring(0, maxLength) + '...';
    }
    return fullName;
};

const EllipsisTag = ({ tagOption, className, onClick, avatarContent, nameContent, tooltipTitle }) => {
    const nameRef = useRef(null);
    const [isOverflow, setIsOverflow] = useState(false);

    const checkOverflow = useCallback(() => {
        const el = nameRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            setIsOverflow(el.scrollWidth > el.clientWidth);
        });
    }, []);

    useEffect(() => {
        checkOverflow();
        const observer = new ResizeObserver(() => checkOverflow());
        if (nameRef.current) {
            observer.observe(nameRef.current);
        }
        return () => observer.disconnect();
    }, [checkOverflow, nameContent]);

    const content = (
        <span className="assign-option" onClick={onClick}>
            {avatarContent}
            <span className="assign-filter-name" ref={nameRef}>
                {nameContent}
            </span>
        </span>
    );

    if (isOverflow) {
        return (
            <Tooltip
                title={tooltipTitle}
                className={className}
                placement="top"
                arrow
            >
                {content}
            </Tooltip>
        );
    }

    return <span className={className}>{content}</span>;
};

const AssigneeFieldCommonUI = ({
    assignList = [],
    onSelectNewAssign,
    disable,
    fieldId,
    isShowIconCopyFieldId,
    uniqueId,
    selectedAssignee,
    isLoading,
    label,
    selectedAssigneeOption,
    isEllipsisField = false
}) => {
    const fieldLabel = label || Constant.SYSTEM_FIELD_NAME.PERSON_IN_CHARGE;

    const buildFallbackOption = (userId) => ({
        id: userId,
        fullName: userId,
        lastName: { jp: { A: 'F', text: userId } }
    });

    const selectedAssigneeData = React.useMemo(() => {
        if (selectedAssigneeOption && selectedAssigneeOption.id) {
            return [{ ...selectedAssigneeOption }];
        }

        let assignee = assignList.filter(option => option.id === selectedAssignee);
        if (!Utils.isEmptyArray(assignee)) {
            return [assignee[0]];
        }

        if (!Utils.isEmptyString(selectedAssignee)) {
            return [buildFallbackOption(selectedAssignee)];
        }

        return [{
            id: Constant.OPTION_UNASSIGNEE.KEY,
            fullName: Constant.OPTION_UNASSIGNEE.TEXT
        }];
    }, [assignList, selectedAssignee, selectedAssigneeOption]);

        const assigneeUserData = selectedAssigneeData?.[0] || {};
    const [open, setOpen] = useState(false);
    const [isMe, setIsMe] = useState(false);

    React.useEffect(() => {
        setIsMe(selectedAssigneeData?.[0]?.fullName === Constant.OPTION_ASSIGNEE_ME);
    }, [selectedAssigneeData]);

    let resultUser = assignList.filter(user => {
        return !Utils.isEmptyArray(assignList) && user.id === assignList[1]?.id;
    });

    const getAssigneeName = (assigneeItem = {}, shouldTruncate = true) => {
        if (assigneeItem?.fullName === Constant.OPTION_UNASSIGNEE.TEXT) {
            return Constant.OPTION_UNASSIGNEE.TEXT;
        }

        let name = assigneeItem?.fullName;
        if (!assigneeItem?.fullName) {
            name = Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, assigneeItem);
        }

        if (assigneeItem?.fullName === Constant.OPTION_ASSIGNEE_ME && !isMe) {
            const user = resultUser[1];
            name = user?.fullName || Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, user);
        }

        return shouldTruncate ? getShortFullName(name, 35) : (name || '');
    };

    const renderAssigneeName = (assigneeItem = {}) => {
        return getAssigneeName(assigneeItem, true);
    };

    const getSelectedOptionAvatar = (currentSelectOption = {}) => {
        if (currentSelectOption.fullName === Constant.OPTION_UNASSIGNEE.TEXT) {
            return <div></div>;
        }

        if (currentSelectOption.avatar) {
            return (
                <img
                    src={currentSelectOption.avatar}
                    alt=""
                    className="assign-filter-avatar-autocomplete assign-filter-img avatar-tag-autocomplete"
                />
            );
        }

        return (
            <div className="assign-filter-icon-autocomplete assign-filter-img">
                {Utils.getShortName(
                    Utils.getTextByLanguage(
                        Constant.LANGUAGE_TYPE.JP,
                        currentSelectOption.lastName || {
                            jp: { A: 'F', text: currentSelectOption.fullName }
                        }
                    )
                )}
            </div>
        );
    };

    let getOptionSelect = (option, selectedValue) => {
        switch (option.fullName) {
            case Constant.OPTION_ASSIGNEE_ME:
                if (isMe) return isMe;
                return false;
            case Constant.OPTION_UNASSIGNEE.TEXT:
                return option.id === selectedValue.id;
            default:
                if (isMe && option.id === assignList[1]?.id) return false;
                return option.id === selectedValue?.id;
        }
    };

    const getFullName = (assigneeItem = {}) => {
        if (assigneeItem?.fullName === Constant.OPTION_UNASSIGNEE.TEXT) {
            return Constant.OPTION_UNASSIGNEE.TEXT;
        }

        let name = assigneeItem?.fullName;
        if (!assigneeItem?.fullName) {
            name = Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, assigneeItem);
        }

        if (assigneeItem?.fullName === Constant.OPTION_ASSIGNEE_ME && !isMe) {
            const user = resultUser[1];
            name = user?.fullName || Utils.getFullNameOfUser(Constant.LANGUAGE_TYPE.JP, user);
        }

        return name || '';
    };

    const getTitle = (selectOption) => {
        switch (selectOption?.fullName) {
            case Constant.OPTION_ASSIGNEE_ME:
                return resultUser[1]?.fullName || Constant.OPTION_ASSIGNEE_ME;
            case Constant.OPTION_UNASSIGNEE.TEXT:
                return Constant.OPTION_UNASSIGNEE.TEXT;
            default:
                return isEllipsisField ? getFullName(selectOption) : renderAssigneeName(selectOption);
        }
    };

    const disabledClassName = disable ? 'class_disabled' : '';

    return (
        <div className={'form-addTicket assignee-field-common-content'}>
            <div className="gap-8">
                <label className="form-addTicket-label">{fieldLabel}</label>
                {isShowIconCopyFieldId && (
                    <Tooltip title={fieldId} placement="top" arrow>
                        <img
                            src="/assets/image/icon-copy.svg"
                            alt="copy"
                            onClick={() => navigator.clipboard.writeText(fieldId)}
                        />
                    </Tooltip>
                )}
            </div>

            <div className="form-addTicket-input">
                <div className={`assign-list-all ${disabledClassName}`}>
                    <Autocomplete
                        open={open}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                        key={`assignee-list-content-sugguest-${uniqueId}`}
                        PopperComponent={({ style, ...popperProps }) => (
                            <Popper
                                {...popperProps}
                                style={{ ...style }} // width is passed in 'style' prop
                                className="assign-filter-content"
                            />
                        )}
                        disabled={disable}
                        className={`assign-filter-text-common ${uniqueId} ${isEllipsisField ? 'telephone-evaluator-ellipsis' : ''}`}
                        // freeSolo
                        multiple={true}
                        sx={{
                            maxHeight: 3
                        }}
                        options={assignList || []}
                        getOptionLabel={option => {
                            let optionLabel = option.fullName || '';
                            if (
                                optionLabel &&
                                !Utils.isEmptyObject(option.affiliationOrganizationInfo) &&
                                Utils.getCorporationNameSuffixText(
                                    option.affiliationOrganizationInfo
                                )
                            ) {
                                optionLabel = `${optionLabel} (${Utils.getCorporationNameSuffixText(
                                    option.affiliationOrganizationInfo
                                )})`;
                            }
                            return optionLabel;
                        }}
                        getOptionSelected={(option, selectedValue) => {
                            return getOptionSelect(option, selectedValue)
                        }}
                        renderTags={valueTag => {
                            return valueTag.map((tagOption, index) => {
                                if (isEllipsisField) {
                                    return (
                                        <EllipsisTag
                                            key={`assign-filter-tooltip-${tagOption.id}`}
                                            tagOption={tagOption}
                                            className={
                                                tagOption.id === assigneeUserData?.id
                                                    ? 'assign-filter-tooltip active'
                                                    : 'assign-filter-tooltip'
                                            }
                                            onClick={() => setOpen(true)}
                                            avatarContent={getSelectedOptionAvatar(tagOption)}
                                            nameContent={getFullName(tagOption)}
                                            tooltipTitle={getTitle(tagOption)}
                                        />
                                    );
                                }
                                return (
                                    <Tooltip
                                        key={`assign-filter-tooltip-${tagOption.id}`}
                                        title={getTitle(tagOption)}
                                        className={
                                            tagOption.id === assigneeUserData?.id
                                                ? 'assign-filter-tooltip active'
                                                : 'assign-filter-tooltip'
                                        }
                                        onClick={e => setOpen(true)}
                                        placement="top"
                                    >
                                        <span
                                            className="assign-option"
                                        >
                                            {getSelectedOptionAvatar(tagOption)}
                                            <span
                                                className="assign-filter-name"
                                            >
                                                {renderAssigneeName(tagOption)}
                                            </span>
                                        </span>
                                    </Tooltip>
                                );
                            });
                        }}
                        value={selectedAssigneeData}
                        filterOptions={filterSugguestionAssignOption}
                        disablePortal={true}
                        renderOption={option => (
                            <span
                                key={`assign-filter-option-${option.id}`}
                                className={
                                    option.id === selectedAssigneeData?.[0]?.id
                                        ? 'option-item assign-filter-tooltip active'
                                        : 'option-item assign-filter-tooltip'
                                }
                            >
                                { option.fullName !== Constant.OPTION_UNASSIGNEE.TEXT ?
                                <span className="assign-option">
                                    {option.avatar && (
                                        <img
                                            src={option.avatar}
                                            alt=""
                                            className="assign-filter-avatar assign-filter-img"
                                        />
                                    )}
                                    {!option.avatar && (
                                        <span className="assign-filter-icon assign-filter-img">
                                            {Utils.getShortName(
                                                Utils.getTextByLanguage(
                                                    Constant.LANGUAGE_TYPE.JP,
                                                    option.lastName || {
                                                        jp: { A: 'F', text: option.fullName }
                                                    }
                                                )
                                            )}
                                        </span>
                                    )}
                                    <span className="assign-filter-name">
                                        <OverFlowTooltipWithTabsMui value={option.fullName} />
                                    </span>
                                </span> 
                                :
                                <span className="assign-option">
                                    <span className="">
                                        <OverFlowTooltipWithTabsMui value={option.fullName} />
                                    </span>
                                </span> 
                                }
                            </span>
                        )}
                        // open
                        onChange={(e, newValue) => {
                            const latestSelected = newValue?.[newValue.length - 1];
                            setIsMe(latestSelected?.fullName === Constant.OPTION_ASSIGNEE_ME);
                            onSelectNewAssign(newValue);
                        }}
                        // noOptionsText={I18n.get('COMMSG001')}
                        renderInput={params => (
                            <RenderOptionTag
                                params={params}
                                assigneeUserData={assigneeUserData}
                            />
                        )}
                    />
                    {Utils.isEmptyArray(assignList) && !isLoading && (
                        <p style={{ marginTop: '10px' }}>{I18n.get('COMMSG001')}</p>
                    )}
                </div>
            </div>
        </div>
    );
};
export default AssigneeFieldCommonUI;

AssigneeFieldCommonUI.propTypes = {
    assignList: PropTypes.any,
    onSelectNewAssign: PropTypes.any,
    disable: PropTypes.any,
    fieldId: PropTypes.any,
    isShowIconCopyFieldId: PropTypes.any,
    uniqueId: PropTypes.any,
    selectedAssignee: PropTypes.any,
    isLoading: PropTypes.any,
    label: PropTypes.string,
    selectedAssigneeOption: PropTypes.any,
    isEllipsisField: PropTypes.bool
};
